import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import { adminJson } from '../utils/adminAuth';

const REPORTS = [
  { v: 'missing_photos', label: 'Missing Photo Report', needs: 'dealer' },
  { v: 'missing_videos', label: 'Missing Video Report', needs: 'dealer' },
  { v: 'window_sticker', label: 'Window Sticker', needs: 'vehicle' },
  { v: 'buyers_guide', label: 'Buyers Guide', needs: 'vehicle' },
  { v: 'invoice', label: 'Invoice', needs: 'invoice' },
];

export default function AdminReports() {
  const navigate = useNavigate();
  const [dealers, setDealers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reportType, setReportType] = useState('missing_photos');
  const [dealerId, setDealerId] = useState('');
  const [vin, setVin] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const report = REPORTS.find(r => r.v === reportType);

  const authHeader = useCallback(() => {
    const token = localStorage.getItem('adminAuth');
    if (!token) { navigate('/admin/login'); return null; }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  useEffect(() => {
    const h = authHeader();
    if (!h) return;
    fetch(`${API_BASE}/admin/dealers?limit=500`, { headers: h })
      .then(adminJson)
      .then(d => setDealers((Array.isArray(d) ? d : (d.dealers || d.dealerships || []))))
      .catch(() => setError('Failed to load dealers'));
  }, [authHeader]);

  // Load vehicles / invoices when dealer changes (for sticker/guide/invoice)
  useEffect(() => {
    const h = authHeader();
    if (!h || !dealerId) { setVehicles([]); setInvoices([]); return; }
    setVin(''); setInvoiceId('');
    if (report.needs === 'vehicle') {
      fetch(`${API_BASE}/photographer/dealerships/${dealerId}/vehicles`, { headers: h })
        .then(adminJson).then(d => setVehicles(Array.isArray(d) ? d : (d.vehicles || []))).catch(() => {});
    } else if (report.needs === 'invoice') {
      fetch(`${API_BASE}/photographer/invoices?dealership_id=${dealerId}`, { headers: h })
        .then(adminJson).then(d => setInvoices(Array.isArray(d) ? d : (d.invoices || []))).catch(() => {});
    }
  }, [authHeader, dealerId, report.needs]);

  const generate = async () => {
    const h = authHeader();
    if (!h) return;
    if (!dealerId) { setError('Select a dealership'); return; }
    let url, name;
    if (reportType === 'missing_photos') { url = `${API_BASE}/photographer/reports/missing-photos/${dealerId}`; name = 'missing-photos.pdf'; }
    else if (reportType === 'missing_videos') { url = `${API_BASE}/photographer/reports/missing-photos/${dealerId}?media=video`; name = 'missing-videos.pdf'; }
    else if (reportType === 'window_sticker') { if (!vin) { setError('Select a vehicle'); return; } url = `${API_BASE}/photographer/reports/window-sticker/${vin}?dealership_id=${dealerId}`; name = `window-sticker-${vin}.pdf`; }
    else if (reportType === 'buyers_guide') { if (!vin) { setError('Select a vehicle'); return; } url = `${API_BASE}/photographer/reports/buyers-guide/${vin}?dealership_id=${dealerId}`; name = `buyers-guide-${vin}.pdf`; }
    else { if (!invoiceId) { setError('Select an invoice'); return; } url = `${API_BASE}/photographer/invoices/${invoiceId}/pdf`; name = `invoice-${invoiceId}.pdf`; }

    setBusy(true); setError('');
    try {
      const res = await fetch(url, { headers: h });
      if (!res.ok) { const e = await res.json().catch(() => ({})); setError(e.detail || `Failed (${res.status})`); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob); a.download = name; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError('Failed to generate report'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ padding: 20, maxWidth: 700, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Reports</h2>
      </div>
      <p style={{ color: '#6b7280', marginTop: 0 }}>Generate a PDF report. It downloads to your browser.</p>
      {error && <div style={{ color: '#dc2626', marginBottom: 8 }}>{error}</div>}

      <div style={{ display: 'grid', gap: 12, maxWidth: 480 }}>
        <label style={lbl}>Report type
          <select value={reportType} onChange={e => setReportType(e.target.value)} style={inp}>
            {REPORTS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
          </select>
        </label>
        <label style={lbl}>Dealership
          <select value={dealerId} onChange={e => setDealerId(e.target.value)} style={inp}>
            <option value="">— select —</option>
            {dealers.map(d => <option key={d.id} value={d.id}>{d.dealership_name || d.name}</option>)}
          </select>
        </label>
        {report.needs === 'vehicle' && (
          <label style={lbl}>Vehicle
            <select value={vin} onChange={e => setVin(e.target.value)} style={inp} disabled={!dealerId}>
              <option value="">— select —</option>
              {vehicles.map(v => <option key={v.vin} value={v.vin}>{v.year} {v.make} {v.model} — {v.vin}{v.stock_number ? ` (${v.stock_number})` : ''}</option>)}
            </select>
          </label>
        )}
        {report.needs === 'invoice' && (
          <label style={lbl}>Invoice
            <select value={invoiceId} onChange={e => setInvoiceId(e.target.value)} style={inp} disabled={!dealerId}>
              <option value="">— select —</option>
              {invoices.map(iv => <option key={iv.id} value={iv.id}>#{iv.invoice_number || iv.id} — {iv.total ? `$${iv.total}` : ''} {iv.created_at ? new Date(iv.created_at).toLocaleDateString() : ''}</option>)}
            </select>
          </label>
        )}
        <button onClick={generate} disabled={busy} style={primaryBtn}>{busy ? 'Generating…' : '⬇ Generate PDF'}</button>
      </div>
    </div>
  );
}

const linkBtn = { background: 'none', border: 'none', color: '#0078D4', cursor: 'pointer', padding: 0 };
const primaryBtn = { padding: '10px 16px', background: '#0078D4', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: 12, color: '#6b7280' };
const inp = { width: '100%', padding: 9, border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box', marginTop: 4 };
